import React, { useState, useEffect } from "react";
import { BookOpen, Loader2, GraduationCap, CheckCircle2, AlertTriangle, RefreshCw, Sparkles, ArrowLeft, Brain, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

const KIND_BADGE = {
  new: { label: "NEW", cls: "bg-primary/10 text-primary" },
  review: { label: "REVISION", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
};

const Stat = ({ label, value, icon: Icon }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={`learn-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <Icon className="h-4 w-4 text-primary mb-3" />
    <p className="font-display text-2xl font-bold">{value}</p>
    <p className="text-[11px] text-muted-foreground mt-1">{label}</p>
  </div>
);

export const LearnTab = () => {
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);

  // lesson / quiz state
  const [active, setActive] = useState(null); // {topic_id, topic_name}
  const [lesson, setLesson] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [p, pr] = await Promise.all([api.get("/learn/plan/today"), api.get("/learn/progress")]);
      setPlan(p.data.plan);
      setProgress(pr.data);
    } catch (err) {
      logger.error("Failed to load learning plan:", err?.message);
    } finally {
      setLoading(false);
      setBuilding(false);
    }
  };

  useEffect(() => {
    setBuilding(true);
    loadAll();
    // Runs once on mount to build/load the daily plan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openLesson = async (item) => {
    setBusy(true);
    setActive(item);
    setLesson(null); setQuiz(null); setAnswers([]); setResult(null);
    try {
      const { data } = await api.get(`/learn/lesson/${item.topic_id}`);
      setLesson(data.lesson);
    } catch (err) {
      logger.error("Failed to load lesson:", err?.message);
    } finally {
      setBusy(false);
    }
  };

  const startQuiz = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/learn/quiz/${active.topic_id}`);
      setQuiz(data);
      setAnswers(new Array(data.questions.length).fill(-1));
      setResult(null);
    } catch (err) {
      logger.error("Failed to load quiz:", err?.message);
    } finally {
      setBusy(false);
    }
  };

  const submitQuiz = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/learn/quiz/submit", { topic_id: active.topic_id, answers });
      setResult(data);
      await loadAll();
    } catch (err) {
      logger.error("Failed to submit quiz:", err?.message);
    } finally {
      setBusy(false);
    }
  };

  const backToPlan = () => {
    setActive(null); setLesson(null); setQuiz(null); setAnswers([]); setResult(null);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4" data-testid="learn-loading">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          {building ? "Fetching your exam syllabus from official sources & building today's plan…" : "Loading…"}
        </p>
      </div>
    );
  }

  const total = progress?.total || 0;
  const mastered = progress?.mastered || 0;
  const pct = total ? Math.round((mastered / total) * 100) : 0;

  // ---- Lesson / Quiz view ----
  if (active) {
    return (
      <div className="space-y-6" data-testid="learn-lesson-view">
        <button onClick={backToPlan} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="learn-back">
          <ArrowLeft className="h-4 w-4" /> Back to today's plan
        </button>

        {busy && !lesson && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

        {lesson && (
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="learn-lesson-card">
            <div className="flex items-center gap-2 mb-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{active.subject}</span>
            </div>
            <h2 className="font-display text-2xl font-bold mb-5">{lesson.title}</h2>
            <div className="prose prose-sm max-w-none text-sm leading-relaxed text-foreground whitespace-pre-wrap" data-testid="learn-lesson-content">
              {lesson.content}
            </div>
            {lesson.key_points?.length > 0 && (
              <div className="mt-6 pt-5 border-t border-border">
                <p className="text-xs font-semibold text-primary mb-3 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Key points to remember</p>
                <ul className="space-y-2">
                  {lesson.key_points.map((k) => (
                    <li key={k} className="text-sm text-muted-foreground flex gap-2"><span className="text-primary">→</span>{k}</li>
                  ))}
                </ul>
              </div>
            )}

            {!quiz && !result && (
              <button onClick={startQuiz} disabled={busy}
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white font-medium px-6 py-3 hover:scale-[1.02] transition-transform disabled:opacity-60"
                data-testid="learn-start-quiz">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />} Test yourself
              </button>
            )}
          </div>
        )}

        {quiz && (
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="learn-quiz-card">
            <h3 className="font-display text-lg font-bold mb-5">Quick quiz · {quiz.topic_name}</h3>
            <div className="space-y-6">
              {quiz.questions.map((q, qi) => {
                const res = result?.results?.[qi];
                return (
                  <div key={`${qi}-${q.q.slice(0, 12)}`} data-testid={`learn-question-${qi}`}>
                    <p className="text-sm font-medium mb-3">{qi + 1}. {q.q}</p>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => {
                        const chosen = answers[qi] === oi;
                        let cls = "border-border hover:border-primary/40";
                        if (result && res) {
                          if (oi === res.answer) cls = "border-emerald-500 bg-emerald-500/10";
                          else if (oi === res.chosen) cls = "border-destructive bg-destructive/10";
                          else cls = "border-border opacity-60";
                        } else if (chosen) cls = "border-primary bg-primary/10";
                        return (
                          <button key={oi} disabled={!!result}
                            onClick={() => setAnswers((a) => { const c = [...a]; c[qi] = oi; return c; })}
                            className={`w-full text-left rounded-xl border px-4 py-2.5 text-sm transition-colors ${cls}`}
                            data-testid={`learn-option-${qi}-${oi}`}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    {result && res?.explanation && (
                      <p className="mt-2 text-xs text-muted-foreground"><span className="font-semibold text-foreground">Why: </span>{res.explanation}</p>
                    )}
                  </div>
                );
              })}
            </div>

            {!result ? (
              <button onClick={submitQuiz} disabled={busy || answers.some((a) => a < 0)}
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white font-medium px-6 py-3 hover:scale-[1.02] transition-transform disabled:opacity-50"
                data-testid="learn-submit-quiz">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Submit answers
              </button>
            ) : (
              <div className="mt-7 rounded-2xl border border-border bg-muted/40 p-5" data-testid="learn-quiz-result">
                <p className="font-display text-xl font-bold">
                  {result.score}/{result.total} correct · {result.percentage}%
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {result.mastered ? "Mastered! " : result.weak ? "This is a weak area — we'll revise it sooner. " : "Nice work. "}
                  Next revision scheduled for <span className="font-medium text-foreground">{result.next_review}</span>.
                </p>
                <button onClick={backToPlan} className="mt-4 inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium hover:border-primary/40" data-testid="learn-finish">
                  Back to today's plan
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ---- Plan overview ----
  const items = plan?.items || [];
  return (
    <div className="space-y-6" data-testid="learn-tab">
      <div className="rounded-2xl border border-amber-400/25 bg-gradient-to-r from-amber-500/10 to-red-600/5 p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <BookOpen className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold">Your AI tutor teaches the full {progress?.exam || "exam"} syllabus — and never lets you forget it.</p>
              <p className="text-sm text-muted-foreground mt-1">
                New topics daily + spaced-repetition revisions. Weak areas automatically come back sooner.
              </p>
            </div>
          </div>
          <button onClick={() => { setBuilding(true); loadAll(); }} className="text-muted-foreground hover:text-foreground" data-testid="learn-refresh" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Mastered" value={`${mastered}/${total}`} icon={GraduationCap} />
        <Stat label="Due today" value={progress?.due_today || 0} icon={CalendarClock} />
        <Stat label="Learning" value={progress?.learning || 0} icon={Brain} />
        <Stat label="Weak areas" value={(progress?.weak_topics || []).length} icon={AlertTriangle} />
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex justify-between text-xs mb-2">
          <span className="text-muted-foreground">Syllabus mastery</span>
          <span className="text-primary font-medium">{pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-red-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-5">
          <Sparkles className="h-4 w-4 text-primary" />
          <h3 className="font-display text-sm font-bold uppercase tracking-wide">Today's lessons</h3>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No lessons scheduled. Try refreshing.</p>
        ) : (
          <div className="space-y-2.5" data-testid="learn-plan-items">
            {items.map((it, i) => {
              const badge = KIND_BADGE[it.kind] || KIND_BADGE.new;
              return (
                <button key={it.topic_id} onClick={() => openLesson(it)}
                  className="w-full flex items-center gap-3 rounded-xl border border-border px-4 py-3 text-left hover:border-primary/40 transition-colors"
                  data-testid={`learn-plan-item-${i}`}>
                  <span className="font-display text-xs font-bold text-muted-foreground w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{it.topic_name}</p>
                    <p className="text-[11px] text-muted-foreground">{it.subject}</p>
                  </div>
                  {it.completed && <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />}
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {(progress?.weak_topics || []).length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <p className="text-xs font-semibold text-destructive mb-3 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Focus areas (revised more often)</p>
          <div className="flex flex-wrap gap-1.5">
            {progress.weak_topics.map((t) => (
              <span key={t.topic_name} className="text-xs rounded-full bg-destructive/10 text-destructive px-2.5 py-1">{t.topic_name}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
